'use client'

import type { ReactNode } from 'react'

import { faAngleDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors } from '@b-stock/fe-core/design-system'

import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'
import { signUpCtaLocationForTab } from '@/helpers/telemetry/signupLinkTracking'

import { lexicalHasContent } from './cmsSellerProfileAbout'
import { SellerProfileLexicalRichText } from './SellerProfileLexicalRichText'
import { AnswerContainer, Container } from './ui'

export const StorefrontAboutContainer = styled(Container)`
  max-width: 50rem;
  margin-left: auto;
  margin-right: auto;
`

// details/summary rather than the design-system Accordion so the copy is indexable as page text.
const ItemDetails = styled.details`
  padding-left: 1rem;
  max-width: 50rem;
  @media ${Breakpoints.max.medium} {
    margin-top: 2rem;
  }
`

const ItemSummary = styled.summary`
  font-size: 1rem;
  line-height: 1.25rem;
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  list-style: none;
  font-weight: 700;

  &::-webkit-details-marker {
    display: none;
  }
  &::marker {
    display: none;
  }
`

const ItemToggleIcon = styled(FontAwesomeIcon)`
  padding: 0.25rem 0 0;
  flex-shrink: 0;
  transition: transform 0.3s;
  font-size: 1rem;
  color: ${Colors.Semantic.Info.highlight};

  ${ItemDetails}[open] & {
    transform: rotate(180deg);
  }
`

const ItemContent = styled.div`
  background-color: ${Colors.Semantic.Neutral.light_grey};
`

type SignInAboutAccordionItem = {
  key: string
  title: ReactNode
  content: ReactNode
}

/** Collapsed `<details>` folds under the sign-in prompt, one per item. */
export function SignInAboutAccordion({
  items,
}: {
  items: SignInAboutAccordionItem[]
}) {
  return (
    <StorefrontAboutContainer>
      {items.map(({ key, title, content }) => (
        <ItemDetails key={key}>
          <ItemSummary>
            {title}
            <ItemToggleIcon icon={faAngleDown} />
          </ItemSummary>
          <ItemContent>
            <AnswerContainer>{content}</AnswerContainer>
          </ItemContent>
        </ItemDetails>
      ))}
    </StorefrontAboutContainer>
  )
}

/**
 * The About tab's CMS content as accordion folds for the sign-in prompt: the intro under
 * `firstFoldTitle`, then one fold per section. A section with neither a title nor a body is an
 * editor part-way through adding one and is skipped; a body with no title gets the generic label.
 */
export function cmsSignInAccordionSections(
  profile: CmsSellerProfile,
  firstFoldTitle: ReactNode
): SignInAboutAccordionItem[] {
  const signUpCtaLocation = signUpCtaLocationForTab(profile.sellerSlug, 'about')
  const items: SignInAboutAccordionItem[] = []
  if (lexicalHasContent(profile.aboutIntro)) {
    items.push({
      key: 'cms-about-intro',
      title: firstFoldTitle,
      content: (
        <SellerProfileLexicalRichText
          data={profile.aboutIntro}
          signUpCtaLocation={signUpCtaLocation}
        />
      ),
    })
  }
  const sections = profile.aboutSections
  if (Array.isArray(sections)) {
    sections.forEach((section, idx) => {
      const titleTrim =
        typeof section.sectionTitle === 'string'
          ? section.sectionTitle.trim()
          : ''
      const hasBody = lexicalHasContent(section.body)
      if (titleTrim.length === 0 && !hasBody) return
      items.push({
        key:
          typeof section.id === 'string' && section.id.length > 0
            ? section.id
            : `cms-about-${idx}`,
        title:
          titleTrim.length > 0 ? (
            titleTrim
          ) : (
            <FormattedMessage id="Storefront.about.cmsSectionUntitled" />
          ),
        content: section.body ? (
          <SellerProfileLexicalRichText
            data={section.body}
            signUpCtaLocation={signUpCtaLocation}
          />
        ) : null,
      })
    })
  }
  return items
}
